import React, { useContext } from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
} from "@mui/material";
import { COVERAGE_FORM } from "@platform/rfp-core";
import { TextField, Typography } from "@platform/design-system";

import { CommissionSection, SectionTitle } from "../../components";
import { RFP_ACTION_TYPES } from "../../constants";
import { RfpContext } from "../../contexts/rfpContext";
import CoverageButtonWrapper from "../components/coverageButtonWrapper";
import CommentsField from "../components/commentsField";

import { createValidationHandlers } from "./coveragesValidations";

const {
  PLAN_TYPE,
  PLANS,
  ALTERNATE_PLANS,
  ADDITIONAL_NOTES,
  CONTRIBUTORY,
  CONTRIBUTION_TYPE,
  CONTRIBUTION_VALUE,
  CONTRIBUTION_STRUCTURE,
  CURRENT_COMMISSION,
  REQUESTED_COMMISSION,
  SINGLE_PLAN,
  TOTAL_EMPLOYER_PAID,
  TOTAL_EMPLOYEE_PAID,
  EMPLOYER_CONTRIBUTION_PERCENTAGE,
  EMPLOYER_CONTRIBUTION_AMOUNT,
  GROSS_UP,
  NO_PLAN,
} = COVERAGE_FORM;

const { UPDATE_SELECTED_COVERAGE } = RFP_ACTION_TYPES;

const initialValues = {
  [CONTRIBUTION_STRUCTURE]: "",
  [CONTRIBUTION_TYPE]: "",
  [CONTRIBUTION_VALUE]: "",
  [CURRENT_COMMISSION]: "",
  [REQUESTED_COMMISSION]: "",
  [ADDITIONAL_NOTES]: "",
  [ALTERNATE_PLANS]: "",
};

export const initialIndividualDisabilityCoveragesDetails = {
  [PLAN_TYPE]: SINGLE_PLAN,
  [PLANS]: [
    {
      ...initialValues,
      name: SINGLE_PLAN,
    },
  ],
};

const IndividualDisability = ({
  coverage,
  handleNext,
  isLastCoverage,
  nextCoverage,
  handleBack,
  prevCoverage,
  isFirstCoverage,
}) => {
  const { setRfp } = useContext(RfpContext);

  const handleFormChange = (updatedCoverage, currentPlanKey, fieldKey) => {
    setRfp({
      type: UPDATE_SELECTED_COVERAGE,
      value: updatedCoverage,
      currentPlanKey,
      fieldKey,
    });
  };

  return (
    <>
      <IndividualDisabilityForm
        coverage={coverage}
        onFormChange={handleFormChange}
      />
      <CoverageButtonWrapper
        displayBackButton={!isFirstCoverage}
        displayNextButton={!isLastCoverage}
        backButtonName={prevCoverage?.name}
        nextButtonName={nextCoverage?.name}
        handleBackAction={handleBack}
        handleNextAction={handleNext}
      />
    </>
  );
};

export const IndividualDisabilityForm = ({ coverage, onFormChange }) => {
  const currentPlan = coverage?.details[PLANS][0];
  const currentPlanErrors = currentPlan.errors || {};
  const isCurrentCoverage = coverage?.isCurrentCoverage;

  const handlePlanFormChange = ({ name, value }) => {
    const localPlan = {
      ...currentPlan,
      [name]: value,
      displayName: NO_PLAN,
    };

    if (name === CONTRIBUTION_TYPE) {
      localPlan[CONTRIBUTION_VALUE] = "";
    }

    const payload = {
      ...coverage,
      details: { ...coverage.details, [PLANS]: [localPlan] },
    };

    onFormChange(payload, 0, name);
  };

  const { handlePlanFormChangeWithValidation, handlePreviousFieldsValidation } =
    createValidationHandlers({
      currentPlan,
      coverage,
      onFormChange,
      isCurrentCoverage,
      activeTab: 0,
    });

  return (
    <>
      <SectionTitle>Contribution Structure</SectionTitle>
      <Box pl={2} pr={2}>
        <FormControl
          onFocus={() => {
            handlePreviousFieldsValidation(CONTRIBUTION_STRUCTURE);
          }}
        >
          <FormLabel id="contribution-structure-label" required>
            Select a Contribution Structure
          </FormLabel>
          <RadioGroup
            value={currentPlan[CONTRIBUTION_STRUCTURE]}
            row
            aria-labelledby="contribution-structure-label"
            name={CONTRIBUTION_STRUCTURE}
            onChange={(e) => {
              handlePlanFormChangeWithValidation(e.target);
            }}
          >
            <FormControlLabel
              value={TOTAL_EMPLOYER_PAID}
              control={<Radio />}
              label={TOTAL_EMPLOYER_PAID}
            />
            <FormControlLabel
              value={TOTAL_EMPLOYEE_PAID}
              control={<Radio />}
              label={TOTAL_EMPLOYEE_PAID}
            />
            <FormControlLabel
              value={CONTRIBUTORY}
              control={<Radio />}
              label={CONTRIBUTORY}
            />
            <FormControlLabel
              value={GROSS_UP}
              control={<Radio />}
              label={GROSS_UP}
            />
          </RadioGroup>
        </FormControl>
        {currentPlan[CONTRIBUTION_STRUCTURE] === CONTRIBUTORY && (
          <Box mt={2}>
            <FormControl>
              <FormLabel id="contribution-amount-label">
                Add Contribution amount
              </FormLabel>
              <Box mt={1}>
                <RadioGroup
                  value={currentPlan[CONTRIBUTION_TYPE]}
                  aria-labelledby="contribution-amount-label"
                  name={CONTRIBUTION_TYPE}
                  onChange={(e) => {
                    handlePlanFormChangeWithValidation(e.target);
                  }}
                >
                  <FormControlLabel
                    value={EMPLOYER_CONTRIBUTION_PERCENTAGE}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" fullWidth>
                        <Typography noWrap mr={1}>
                          {EMPLOYER_CONTRIBUTION_PERCENTAGE}
                        </Typography>
                        {currentPlan[CONTRIBUTION_TYPE] ===
                          EMPLOYER_CONTRIBUTION_PERCENTAGE && (
                          <TextField
                            autoComplete="off"
                            size="sm"
                            name={CONTRIBUTION_VALUE}
                            value={currentPlan[CONTRIBUTION_VALUE] || ""}
                            onChange={(e) => {
                              handlePlanFormChange(e.target);
                            }}
                            onBlur={(e) => {
                              handlePlanFormChangeWithValidation(e.target);
                            }}
                            label={<Typography>% percentage</Typography>}
                          />
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value={EMPLOYER_CONTRIBUTION_AMOUNT}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" fullWidth>
                        <Typography noWrap mr={1}>
                          {EMPLOYER_CONTRIBUTION_AMOUNT}
                        </Typography>
                        {currentPlan[CONTRIBUTION_TYPE] ===
                          EMPLOYER_CONTRIBUTION_AMOUNT && (
                          <TextField
                            autoComplete="off"
                            size="sm"
                            name={CONTRIBUTION_VALUE}
                            value={currentPlan[CONTRIBUTION_VALUE] || ""}
                            onChange={(e) => {
                              handlePlanFormChange(e.target);
                            }}
                            onBlur={(e) => {
                              handlePlanFormChangeWithValidation(e.target);
                            }}
                            label={<Typography>$ amount</Typography>}
                          />
                        )}
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>
            </FormControl>
          </Box>
        )}
        <Box>
          {!!currentPlanErrors && (
            <Typography variant="caption" color="danger">
              {currentPlanErrors[CONTRIBUTION_STRUCTURE]}
            </Typography>
          )}
        </Box>
      </Box>

      <CommissionSection
        handlePlanFormChange={handlePlanFormChange}
        handlePlanFormChangeWithValidation={handlePlanFormChangeWithValidation}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
        currentPlanErrors={currentPlanErrors}
        currentPlan={currentPlan}
        isCurrentCoverage={isCurrentCoverage}
      />

      <CommentsField
        currentPlan={currentPlan}
        onChange={(e) => {
          handlePlanFormChange(e.target);
        }}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
      />
    </>
  );
};

export default IndividualDisability;
